import type { InitFormRequest } from 'apiConnector/batch-workspace';
import { batchWorkspaceApiConnector } from 'apiConnector/batch-workspace';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import type { BatchFormResult } from 'models/forms/BatchFormResult';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { of } from 'rxjs';
import { catchError, mergeMap, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import { defaultBatchFormFailed, defaultBatchFormTimeouted } from 'state/batchForm/batchForm.action';
import { defaultBatchFormReceivedThunk } from 'state/batchForm/batchForm.thunks';
import type { EpicWithThunk } from 'state/state.common';
import { getTodayStringInLocalTimezone } from 'utils/core/date';

export interface FetchDefaultFormEpicApi {
  initForm(request: InitFormRequest): Observable<BatchFormResult>;
}
export function fetchDefaultBatchFormEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (
    actions$,
    state$,
    {
      httpClient,
      selectors: {
        getUserPreferencesCurrency,
        isUserPreferencesPrivateMode,
        getUserPreferencesSelectedOnBehalfClient,
        getUserPreferencesXMargin,
      },
    },
  ) => {
    return actions$.pipe(
      ofType('DEFAULT_BATCH_FORM_REQUESTED'),
      withLatestFrom(state$),
      mergeMap(([{ tabId, temporaryFormId, productSubType, wrapper, payoffType, productType }, state]) => {
        const tabRemoved$ = tabRemoved(actions$, tabId, state$);
        const tradeDate = getTodayStringInLocalTimezone();
        const isPrivateMode = isUserPreferencesPrivateMode(state);
        const currency = getUserPreferencesCurrency(state);
        const selectedOnBehalfClient = getUserPreferencesSelectedOnBehalfClient(state);
        const xMargin = getUserPreferencesXMargin(state);

        return batchWorkspaceApiConnector(httpClient)
          .initForm({
            productType,
            productSubType,
            wrapper,
            payoffType,
            tradeDate,
            isPrivateMode,
            currency,
            selectedOnBehalfClient,
            xMargin,
          })
          .pipe(
            takeUntil(tabRemoved$),
            mergeMap(
              ({
                fieldsMetadata,
                productView,
                values,
                version,
                negotiationId,
                actions,
                quoteQuery,
                errors,
                mustForceAdvancedMode,
                allowedRequests,
              }) =>
                of(
                  defaultBatchFormReceivedThunk(
                    tabId,
                    temporaryFormId,
                    negotiationId,
                    productView,
                    actions,
                    values,
                    fieldsMetadata,
                    quoteQuery,
                    version,
                    mustForceAdvancedMode ?? false,
                    allowedRequests,
                    errors,
                  ),
                ),
            ),
            catchError(_ => of(defaultBatchFormFailed(temporaryFormId))),
            timeoutWith(timeoutInMillis, of(defaultBatchFormTimeouted(temporaryFormId)), appScheduler),
          );
      }),
    );
  };
}
